import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import sleep from 'sleep-promise';
import Fetch from '../../../../core/util/core/Fetch';
import CustomForm from '../../../../core/components/core/Util/Form/CustomForm/CustomForm';
import CustomFormContainer from '../../../../core/components/core/Util/Form/CustomForm/CustomFormContainer';
import CustomList from '../../../../core/components/core/Util/List/CustomList/CustomList';
import CustomListContainer from '../../../../core/components/core/Util/List/CustomList/CustomListContainer';
import { loadData } from '../../../../core/components/core/Util/List/CustomList/loadData';
import config from '../../../config/config';
import { chooseProfileFilter } from '../../../helpers/chooseProfileFilter';
import { VerifyAction } from '../../../../core/util/core/VerifyPermission';

function PaymentEditModal({ item, closeModal, getForm, getDocumentNumberRequired, paths, list }) {
    const [content, setContent] = useState(<>Carregando...</>);

    useEffect(() => {
        const load = async () => {
            const values = await Fetch.post(paths.endpointGetById, { id: item.id });
            const docNumberRequired = await getDocumentNumberRequired(values);
            const form = getForm(values, docNumberRequired);

            form.id = item.id;
            form.readOnly = false;
            form.labelTitle = form.labelTitleEdit;
            form.key_data_rows = list.key_data_rows;
            form.loadList = loadData;

            setContent(
                <CustomFormContainer
                    form={form}
                    list={list}
                    content={<CustomForm form={form} list={list} />}
                />
            );
        };
        load();
    }, [item.id]);

    return content;
}

function List({ formValues }) {
    const profileFilter = chooseProfileFilter();
    const dispatch = useDispatch();

    const pathFrontEnd = '/gci/expenses-payments/';
    const endpointBackend = config.endpoints.backend;

    const canEditExpensePayment = VerifyAction('alterar_baixa_despesa');

    const paths = {
        pathAdd: pathFrontEnd + 'add',
        pathEdit: pathFrontEnd + 'edit',
        pathView: pathFrontEnd + 'view',
        endpointDelete: `${endpointBackend}gci/expenses-payments/delete`,
        endpoint: `${endpointBackend}gci/expenses-payments/getall`,
        endpointSave: `${endpointBackend}gci/expenses-payments/save`,
        endpointGetById: `${endpointBackend}gci/expenses-payments/getById`,
        endpointGetAllDocumentTypes: `${endpointBackend}gci/revenues-document-types/getall`,
        endpointGetAllBankAccounts: `${endpointBackend}gci/bank-accounts/getall`,
    };

    const labels = {
        labelTitleAdd: "Adicionar Pagamento",
        labelTitleEdit: "Editar Pagamento",
        labelTitleView: "Visualizar Pagamento"
    };

    const getDocumentNumberRequired = async (values) => {
        const documentTypeId = values?.gci_revenue_document_type_id;
        if (!documentTypeId) return false;
        const documentType = await Fetch.post(`${endpointBackend}gci/revenues-document-types/getById`, { id: documentTypeId });
        return documentType?.need_document_number === '1';
    };

    const getForm = (values, documentNumberRequired = false) => {
        const form = {
            containerType: 'dialog',
            validateOnChange: true,
            hiddenValues: {},
            fields: [
                {
                    name: 'payment_date',
                    type: 'date',
                    label: 'Data de Pagamento',
                    validations: { required: true },
                    columnSize: 12
                },
                {
                    name: 'remaining_value',
                    type: 'decimal',
                    label: 'Valor Restante (Desconsiderando Pagamento atual)',
                    validations: { required: false },
                    readOnly: true,
                    columnSize: 12
                },
                {
                    name: 'value',
                    type: 'decimal',
                    label: 'Valor Pago',
                    validations: {
                        required: true,
                        validateValue: true
                    },
                    columnSize: 12
                },
                {
                    name: 'gci_revenue_document_type_id',
                    type: 'select',
                    label: 'Tipo Doc',
                    options: [],
                    optionsEndpoint: {
                        endpoint: paths.endpointGetAllDocumentTypes,
                        label: 'name',
                        value: 'id',
                    },
                    validations: { required: false },
                    columnSize: 12,
                    handleChange: async (element, field, values) => {
                        const docNumberRequired = await getDocumentNumberRequired(values);
                        const updatedForm = getForm(values, docNumberRequired);
                        dispatch({ type: 'UPDATE_SESSION', key: 'custom_form_content_modal', value: <></> });
                        await sleep(1);
                        dispatch({
                            type: 'UPDATE_SESSION',
                            key: 'custom_form_content_modal',
                            value: (
                                <CustomFormContainer
                                    form={updatedForm}
                                    list={list}
                                    content={<CustomForm form={updatedForm} list={list} />}
                                />
                            )
                        });
                    }
                },
                {
                    name: 'document_number',
                    type: 'text',
                    label: 'Número do Doc',
                    validations: { required: documentNumberRequired },
                    columnSize: 12
                },
                {
                    name: 'gci_bank_account_id',
                    type: 'select',
                    label: 'Conta Bancária',
                    options: [],
                    optionsEndpoint: {
                        endpoint: paths.endpointGetAllBankAccounts,
                        label: 'full_name',
                        value: 'id',
                        params: {
                            filter: { ...profileFilter }
                        }
                    },
                    validations: { required: false },
                    columnSize: 12
                },
            ],
            customValidators: {
                validateValue: (value, options, key, attributes) => {
                    const numericValue = parseFloat(value || 0);
                    const remaining = parseFloat(attributes.remaining_value);

                    if (numericValue <= 0) {
                        return '^O valor pago deve ser maior que 0.';
                    } else if (numericValue > remaining) {
                        return '^O valor pago não pode ser maior que o valor restante.';
                    }

                    return '';
                }
            },
            ...paths,
            ...labels
        };
        if (values) {
            form.values = values;
        }
        return form;
    };

    const form = getForm();

    const filters = {
        validateOnChange: true,
        showGoBackButton: false,
        showSubmitButton: false,
        handleSubmit: loadData,
        fields: [
            {
                name: 'payment_date',
                name_start: 'payment_date_start',
                name_end: 'payment_date_end',
                type: 'date_period',
                label: 'Data de Pagamento',
                validations: { required: false },
                columnSize: 4,
            },
            {
                name: 'filtro',
                type: 'submit',
                label: 'Pesquisar',
                columnSize: 1
            },
            {
                name: 'reset',
                type: 'reset',
                label: 'Limpar',
                columnSize: 1
            },
        ]
    };

    let list = {
        type: 'dialog',
        labelTitle: 'Lista de Pagamentos',
        fieldsShowOnDelete: ['value'],
        formFilters: filters,
        filter: {
            gci_expense_id: formValues?.id,
            ...profileFilter
        },
        columns: [
            {
                name: 'payment_date',
                label: 'Data de Pagamento',
            },
            {
                name: 'value',
                label: 'Valor',
            },
            {
                name: 'document_type',
                label: 'Tipo de Documento',
            },
            {
                name: 'document_number',
                label: 'Número do Documento',
            },
            {
                name: 'bank_account',
                label: 'Conta Bancária',
                width: '30%'
            }
        ],
        order: [['payment_date', 'DESC']],
        canEditRow: () => canEditExpensePayment,
        canDeleteRow: () => canEditExpensePayment,
        customEditContent: (item, closeModal) => (
            <PaymentEditModal
                item={item}
                closeModal={closeModal}
                getForm={getForm}
                getDocumentNumberRequired={getDocumentNumberRequired}
                paths={paths}
                list={list}
            />
        )
    };

    list = { ...list, ...paths, form };

    return (
        <div>
            <CustomListContainer
                pathAdd={list.pathAdd}
                canAdd={false}
                labelTitle={list.labelTitle}
                labelTitleButtonAdd={list.labelTitleButtonAdd}
                content={<CustomList list={{ ...list, ...paths }} />}
            />
        </div>
    );
}

export default List;
